import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Compass, Users, Calendar, Heart, ArrowRight, CheckCircle,
  Sparkles, Mountain, HandHeart, Zap, BookOpen, MessageCircle, Edit3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const coreModules = [
  {
    id: 'curriculum',
    title: 'The 4-Week Journey',
    description: 'Identity, Community, Stewardship, Resilience—foundations for the working soul.',
    icon: BookOpen,
    color: 'from-amber-500 to-orange-600',
    link: '/faith/curriculum'
  },
  {
    id: 'tribes',
    title: 'Find Your Tribe',
    description: 'Discover communities—spiritual, secular, justice-focused—that fit your values.',
    icon: Users,
    color: 'from-emerald-500 to-teal-600',
    link: '/faith/tribes'
  },
  {
    id: 'practice',
    title: 'Daily Practice',
    description: 'Short, grounded exercises you can do during your workday. 1-5 minutes.',
    icon: Heart,
    color: 'from-purple-500 to-indigo-600',
    link: '/faith/practice'
  },
  {
    id: 'journal',
    title: 'Reflection Journal',
    description: 'Save and review your reflections, gratitude, and spiritual insights over time.',
    icon: Edit3,
    color: 'from-stone-500 to-stone-700',
    link: '/faith/journal'
  }
];

const beliefs = [
  { icon: Sparkles, text: 'Every person has inherent dignity.' },
  { icon: Mountain, text: 'Work is a form of contribution to collective good.' },
  { icon: HandHeart, text: 'Community is essential. Isolation is the enemy.' },
  { icon: Zap, text: 'Resilience is not about never falling—it\'s about getting back up.' }
];

export default function FaithHub() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/faith/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-amber-50/30" data-testid="faith-hub">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-stone-800 to-amber-900" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-6 bg-amber-500/20 text-amber-300 border-amber-500/30 px-4 py-1.5">
                <Compass className="h-4 w-4 mr-2" />
                Faith Foundation (F1)
              </Badge>
              
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Faith wUrks
                <span className="block text-amber-400">Spiritual Foundation</span>
              </h1>
              
              <p className="text-lg md:text-xl text-stone-300 mb-8 max-w-2xl mx-auto">
                A non-denominational spiritual backbone for the working-class individual. 
                Belief in something bigger. Dignity for all. Unity in action.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/faith/curriculum">
                  <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-stone-900 font-semibold px-8">
                    Begin Your Journey
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/faith/practice">
                  <Button size="lg" variant="outline" className="border-stone-400 text-stone-200 hover:bg-stone-800">
                    Today's Practice
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Progress Section (for logged-in users) */}
      {user && stats && (
        <section className="py-8 -mt-8 relative z-20">
          <div className="container mx-auto px-4">
            <Card className="max-w-4xl mx-auto bg-white/90 backdrop-blur shadow-xl border-amber-200">
              <CardContent className="py-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-amber-600">{stats.modules_completed}/{stats.total_modules}</div>
                    <p className="text-sm text-stone-600">Modules Complete</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-amber-600">{stats.practices_completed}</div>
                    <p className="text-sm text-stone-600">Practices Done</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-amber-600">{stats.conversations_with_guide}</div>
                    <p className="text-sm text-stone-600">Guide Chats</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-amber-600">{stats.badges_earned}</div>
                    <p className="text-sm text-stone-600">Badges Earned</p>
                  </div>
                </div>
                {stats.curriculum_progress > 0 && (
                  <div className="mt-6">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-stone-600">Journey Progress</span>
                      <span className="font-medium text-amber-600">{stats.curriculum_progress}%</span>
                    </div>
                    <Progress value={stats.curriculum_progress} className="h-2" />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Core Modules */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">Three Paths, One Purpose</h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Each module is designed for the working person. No jargon. No fluff. Just practical spirituality.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {coreModules.map((module, idx) => (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link to={module.link}>
                  <Card className="h-full hover:shadow-xl transition-all duration-300 group cursor-pointer border-2 border-transparent hover:border-amber-300">
                    <CardHeader>
                      <div className={`p-4 rounded-xl bg-gradient-to-br ${module.color} text-white w-fit mb-4 group-hover:scale-110 transition-transform`}>
                        <module.icon className="h-8 w-8" />
                      </div>
                      <CardTitle className="text-xl text-stone-800">{module.title}</CardTitle>
                      <CardDescription className="text-stone-600">{module.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center text-amber-600 font-medium">
                        Explore <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Beliefs Section */}
      <section className="py-16 bg-stone-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
              What We Hold To Be True
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {beliefs.map((belief, idx) => (
                <motion.div
                  key={`item-${idx}`}
                  initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-4 p-4 rounded-lg bg-stone-700/50"
                >
                  <div className="p-2 rounded-lg bg-amber-500/20">
                    <belief.icon className="h-6 w-6 text-amber-400" />
                  </div>
                  <p className="text-stone-200 text-lg">{belief.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI Faith Guide CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Card className="max-w-3xl mx-auto bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="py-10 px-8 text-center">
              <MessageCircle className="h-12 w-12 text-amber-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-stone-800 mb-4">Meet Your Faith Guide</h3>
              <p className="text-stone-600 mb-6 max-w-xl mx-auto">
                An AI companion that walks with you—not above you. Ask questions, share struggles, 
                explore the curriculum together. Plain-spoken. No judgment.
              </p>
              <Link to="/faith/curriculum">
                <Button className="bg-amber-600 hover:bg-amber-700">
                  Start a Conversation
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 bg-stone-100">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-8">Who This Is For</h2>
            <div className="grid md:grid-cols-2 gap-4 text-left">
              {[
                "The person working the overnight shift who wonders if their life matters.",
                "The single parent juggling two jobs who hasn't been to church in years.",
                "The skeptic who still feels something stirring.",
                "The believer who's tired of institutions but not tired of faith.",
                "Anyone who wants to ground their hustle in something deeper."
              ].map((text, idx) => (
                <div key={`item-${idx}`} className="flex items-start gap-3 p-3">
                  <CheckCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-stone-700">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
